/**
 * Shared per-entity ViewSwitcher configuration for v2 list pages (ADV-787).
 *
 * Each entry defines the canonical tab set and the default tab. Pages still
 * own their tab state (URL/query-string wiring lives per-page), but they all
 * source the labels and default-tab choice from this map for consistency.
 *
 * Stub tabs ("Board view ships in ADV-Board", "Analytics ships in Wave 2")
 * are rendered by the page's switch on `view` — this map only describes the
 * available options.
 */

interface ListPageView {
  value: string;
  label: string;
}

interface ListPageViewConfig {
  options: ListPageView[];
  defaultValue: string;
}

export const LIST_PAGE_VIEWS = {
  opportunities: {
    options: [
      { value: "board", label: "Board" },
      { value: "table", label: "Table" },
      { value: "analytics", label: "Analytics" },
    ],
    // ADV-1359: Board is the data-dense default (ADR-0019 §1.1) now that the
    // real kanban renders IN PLACE. The ADV-787 table-default workaround (board
    // was an EmptyState stub on a separate route) no longer applies.
    defaultValue: "board",
  },
  jobs: {
    // ADV-786: Calendar is the primary jobs view (§1.1 — jobs are scheduled
    // work; the calendar answers "what's happening when") and the default.
    options: [
      { value: "calendar", label: "Calendar" },
      { value: "table", label: "Table" },
      { value: "board", label: "Board" },
      { value: "analytics", label: "Analytics" },
    ],
    defaultValue: "calendar",
  },
  customers: {
    options: [
      { value: "table", label: "Table" },
      { value: "analytics", label: "Analytics" },
    ],
    defaultValue: "table",
  },
  invoices: {
    options: [
      { value: "aging", label: "AR Aging" },
      { value: "table", label: "Table" },
      { value: "analytics", label: "Analytics" },
    ],
    defaultValue: "aging",
  },
  "team-members": {
    options: [
      { value: "table", label: "Table" },
      { value: "analytics", label: "Analytics" },
    ],
    defaultValue: "table",
  },
  crews: {
    options: [
      { value: "crews", label: "Crews" },
      { value: "table", label: "Table" },
    ],
    defaultValue: "crews",
  },
  proposals: {
    options: [
      { value: "table", label: "Table" },
      { value: "pipeline", label: "Pipeline" },
      { value: "analytics", label: "Analytics" },
    ],
    defaultValue: "table",
  },
} as const satisfies Record<string, ListPageViewConfig>;
